/**
 * One element made draggable by a handle — Pragmatic's `draggable()`, bound to React's lifecycle.
 *
 * ── What this adds over calling `draggable()` directly ───────────────────────────────────────
 * Three things, and each of them is a bug the callers would otherwise write one at a time:
 *
 * · **The binding survives a re-render.** `draggable()` returns a cleanup and keys on the element,
 *   so an effect that re-ran on every render would tear the drag down mid-gesture. The payload is
 *   read from a REF inside `getInitialData` rather than closed over, so the effect depends only on
 *   whether dragging is allowed at all — a card whose session name changes while it is being
 *   dragged does not unbind.
 * · **`isDragging` is React state, set from the adapter's own events**, so the source can render the
 *   canon's "Sesja w ruchu" ghost without polling anything.
 * · **The handle is a separate element.** The canon's grip is a 6-dot square in the card header and
 *   the whole card is what moves; `dragHandle` is exactly that, and passing the card as both would
 *   make every click inside it start a drag.
 *
 * The native drag preview is left alone deliberately — see `DragHandle`'s header for what that costs
 * against the canon and why the alternative was declined.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

/**
 * The COMPONENT owns the refs and hands them in, rather than the hook returning them.
 *
 * Not a style choice: a ref reached through a returned object is a ref accessed during render as far
 * as the React Compiler's lint is concerned, and `ref={drag.ref}` is an error under
 * `react-hooks/refs`. A plain `useRef` used directly in `ref=` is the shape the rule is written for,
 * and it also keeps the two elements' identities visible where they are attached.
 */
export function useDraggableItem(options: {
  /** The element that MOVES. */
  readonly ref: RefObject<HTMLElement | null>;
  /** The grip. Nothing else in the element starts a drag. Omit to make the whole element the handle. */
  readonly handleRef?: RefObject<HTMLElement | null>;
  /** Attached to the drag once, at the moment it starts. Read through a ref — see the header. */
  readonly data: Record<string, unknown>;
  /** `false` binds nothing at all, so no gesture can begin. */
  readonly canDrag: boolean;
}): { readonly isDragging: boolean } {
  const [isDragging, setDragging] = useState(false);
  const { ref, handleRef, data, canDrag } = options;

  // The latest payload, without it becoming an effect dependency. Written in an EFFECT rather than
  // during render, which is the rule `react-hooks/refs` enforces — and it is safe here because the
  // only reader is a callback the adapter invokes during a gesture, long after effects have run.
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    const element = ref.current;
    const handle = handleRef?.current;
    if (element === null) return;
    if (!canDrag) return;

    return draggable({
      element,
      ...(handle == null ? {} : { dragHandle: handle }),
      getInitialData: () => dataRef.current,
      onDragStart: () => {
        setDragging(true);
      },
      onDrop: () => {
        setDragging(false);
      },
    });
  }, [ref, handleRef, canDrag]);

  return { isDragging };
}
